import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useVideoEditor } from '../context/VideoEditorContext';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import {
  getActiveClipEffectItems,
  getContainedMediaRect,
  splitSubtitleSegments,
  mapSubtitleSegmentsToTextRanges,
  getCurrentSegment,
  resolveSubtitleAppearance,
  resolveSubtitleInlineStyle,
  resolveSubtitleInlineFontScale,
  sliceSubtitleTextRuns,
  resolveSubtitleLayout,
  type SubtitleSegmentRange,
  type SubtitleTextRun,
} from '@packages/global';
import ClipEffectsRenderer from '../clip-effects/ClipEffectsRenderer';
import { useClipEffectsPlayer } from '../clip-effects/useClipEffectsPlayer';

export default function PreviewTab() {
  const { t } = useTranslation(['editor', 'common']);
  const { state, actions } = useVideoEditor();
  const { currentPlaybackInfo, isPlaying, subtitleEnabled, previewVideoTime, editConfig, selectedClip } = state;

  const [showOverlays] = useState(false);
  const [mainImage, setMainImage] = useState<'start' | 'end' | 'storyboard'>('start');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [playbackAudio, setPlaybackAudio] = useState<HTMLAudioElement | null>(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number>(16 / 9);
  const [imageAspectRatio, setImageAspectRatio] = useState<number>(16 / 9);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [subtitleFontFamily, setSubtitleFontFamily] = useState<string>('');
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewContainerSize, setPreviewContainerSize] = useState({ width: 0, height: 0 });

  // 获取轨道启用状态（BGM 现在由 Context 管理）
  const trackEnabled = {
    video: editConfig?.tracks?.video?.enabled ?? true,
    audio: editConfig?.tracks?.audio?.enabled ?? true,
    subtitle: editConfig?.tracks?.subtitle?.enabled ?? true,
    bgm: editConfig?.tracks?.bgm?.enabled ?? true
  };

  // 非播放状态优先用 selectedClip，避免音频时长超出视频时长时 currentPlaybackInfo 定位不准
  // 播放时用 currentPlaybackInfo?.clip 保证实时跟随播放位置
  const videoClip = isPlaying ? (currentPlaybackInfo?.clip ?? selectedClip) : (selectedClip ?? currentPlaybackInfo?.clip);
  const subtitleTrackStyle = editConfig?.tracks?.subtitle?.style;
  const clipInfo = editConfig?.clips?.find(c => c.clipId === videoClip?.id);
  const activeClipDuration = clipInfo?.duration ?? videoClip?.duration ?? 0;
  const clipDialogueVolume = clipInfo?.dialogueVolume ?? 1;

  // 切换视频片段时，重置 mainImage 为开始帧
  useEffect(() => {
    setMainImage('start');
  }, [videoClip?.id]);

  useEffect(() => {
    const fontUrl = subtitleTrackStyle?.fontUrl;
    const fontKey = subtitleTrackStyle?.fontId || subtitleTrackStyle?.fontName || fontUrl;

    if (!fontUrl || !fontKey) {
      setSubtitleFontFamily('');
      return;
    }

    const family = `VideoAgentSubtitleFont-${String(fontKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    let cancelled = false;

    const loadFont = async () => {
      try {
        const fontFace = new FontFace(family, `url(${fontUrl})`);
        await fontFace.load();
        document.fonts.add(fontFace);
        if (!cancelled) {
          setSubtitleFontFamily(family);
        }
      } catch (err) {
        console.error('Failed to load subtitle font:', err);
        if (!cancelled) {
          setSubtitleFontFamily('');
        }
      }
    };

    loadFont();

    return () => {
      cancelled = true;
    };
  }, [subtitleTrackStyle?.fontId, subtitleTrackStyle?.fontName, subtitleTrackStyle?.fontUrl]);

  const hasVideo = !!videoClip?.videoUrl;
  const hasScriptAudio = !!videoClip?.scriptAudioUrl;
  const hasSoundEffect = !!videoClip?.soundEffectAudioUrl;
  const hasStartImage = !!videoClip?.startImage;
  const hasEndImage = !!videoClip?.endImage;
  const hasStoryboardImage = !!videoClip?.storyboardImage;
  const hasImages = hasStartImage || hasEndImage || hasStoryboardImage;
  const isGenerating = videoClip?.status === 'image_generation' ||
    videoClip?.status === 'voice_generation' ||
    videoClip?.status === 'video_generation';

  // Cleanup old playback audio when clip changes
  useEffect(() => {
    return () => {
      if (playbackAudio) {
        playbackAudio.pause();
      }
      // BGM cleanup moved to separate effect
    };
  }, [videoClip?.id, playbackAudio]);

  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setPreviewContainerSize({ width: rect.width, height: rect.height });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 同步视频播放
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentPlaybackInfo || !hasVideo) return;

    const { localTime, trimStart, trimEnd } = currentPlaybackInfo;

    // 如果有手动设置的时间，优先使用
    const videoTime = previewVideoTime !== null ? previewVideoTime : trimStart + localTime;

    // 获取当前clip的音量设置和轨道音量
    const clipEditInfo = editConfig?.clips?.find(c => c.clipId === videoClip?.id);
    const clipVideoVolume = clipEditInfo?.videoVolume ?? 0; // 默认静音
    const trackVideoVolume = editConfig?.tracks?.video?.volume ?? 1;

    // 控制视频音轨：轨道禁用或音量为0时静音，否则应用音量
    if (!trackEnabled.video || trackVideoVolume === 0) {
      video.muted = true;
    } else {
      video.muted = false;
      video.volume = clipVideoVolume * trackVideoVolume;
    }

    if (isPlaying && previewVideoTime === null) {
      // 正常播放模式
      if (videoTime >= trimEnd) {
        if (!video.paused) {
          video.pause();
        }
        return;
      }

      if (video.paused) {
        video.currentTime = videoTime;
        video.play().catch(console.error);
      } else {
        if (Math.abs(video.currentTime - videoTime) > 0.2) {
          video.currentTime = videoTime;
        }
      }
    } else {
      // 暂停或手动设置时间模式
      video.pause();
      if (previewVideoTime !== null) {
        video.currentTime = previewVideoTime;
      } else {
        video.currentTime = videoTime;
      }
    }
  }, [currentPlaybackInfo, isPlaying, hasVideo, previewVideoTime, trackEnabled.video, editConfig, videoClip?.id]);

  // 播放音频
  useEffect(() => {
    // Clean up any previous playback audio first
    if (playbackAudio) {
      playbackAudio.pause();
      setPlaybackAudio(null);
    }

    const audioVolume = editConfig?.tracks?.audio?.volume ?? 0.8;
    const finalAudioVolume = audioVolume * clipDialogueVolume;

    if (isPlaying && videoClip && trackEnabled.audio && finalAudioVolume > 0) {
      // Play audio - create independent Audio object if needed
      if (hasScriptAudio || hasSoundEffect) {
        // Try to use existing audio element first
        if (audioRef) {
          audioRef.volume = finalAudioVolume;
          audioRef.play().catch(console.error);
        } else {
          // If no audio element in DOM (overlays hidden), create independent Audio object
          const audioUrl = videoClip.scriptAudioUrl || videoClip.soundEffectAudioUrl;
          if (audioUrl) {
            const audio = new Audio(audioUrl);
            audio.volume = finalAudioVolume;
            audio.play().catch(console.error);
            setPlaybackAudio(audio);
          }
        }
      }
    } else {
      if (audioRef) audioRef.pause();
    }
  }, [isPlaying, videoClip?.id, hasScriptAudio, hasSoundEffect, videoClip?.scriptAudioUrl, videoClip?.soundEffectAudioUrl, trackEnabled.audio, editConfig?.tracks?.audio?.volume, clipDialogueVolume]);

  // BGM 现在由 VideoEditorContext 中的 useBgmPlayer 管理，不再在这里处理

  // 监听视频尺寸变化
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateAspectRatio = () => {
      if (video.videoWidth && video.videoHeight) {
        setVideoAspectRatio(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener('loadedmetadata', updateAspectRatio);
    return () => video.removeEventListener('loadedmetadata', updateAspectRatio);
  }, [videoClip?.videoUrl]);

  // 监听图片尺寸变化
  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    const updateImageAspectRatio = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImageAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };

    if (img.complete) {
      updateImageAspectRatio();
    } else {
      img.addEventListener('load', updateImageAspectRatio);
      return () => img.removeEventListener('load', updateImageAspectRatio);
    }
  }, [mainImage === 'start' ? videoClip?.startImage : mainImage === 'end' ? videoClip?.endImage : videoClip?.storyboardImage]);

  const subtitleLayout = resolveSubtitleLayout(videoAspectRatio, subtitleTrackStyle?.fontSize ?? 18);
  const displaySubtitleScript = videoClip?.script || '';
  const subtitleStyle = {
    fontSize: `${subtitleLayout.fontSizeScale}rem`,
    maxWidth: `${subtitleLayout.maxWidthPercent}%`,
    bottom: subtitleLayout.bottomOffset,
    top: undefined as string | undefined,
  };
  const subtitleAppearance = resolveSubtitleAppearance(
    subtitleTrackStyle,
    subtitleTrackStyle?.fontSize ?? 18,
  );
  const subtitleTextStyle: React.CSSProperties = useMemo(() => ({
    fontSize: subtitleStyle.fontSize,
    color: subtitleAppearance.textColor,
    fontFamily: subtitleFontFamily || 'inherit',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    whiteSpace: 'pre-wrap',
    hyphens: 'none',
    textShadow: subtitleAppearance.cssTextShadow,
    display: 'inline-block',
    maxWidth: '100%',
  }), [subtitleAppearance.cssTextShadow, subtitleAppearance.textColor, subtitleStyle.fontSize, subtitleFontFamily]);

  const subtitleContainerPosition = subtitleTrackStyle?.position === 'center'
    ? { top: '50%', transform: 'translate(-50%, -50%)' }
    : { bottom: subtitleStyle.bottom, transform: 'translateX(-50%)' };

  // 计算当前应显示的字幕文本（按分段）
  const currentSubtitleSegment = useMemo<SubtitleSegmentRange | null>(() => {
    if (!displaySubtitleScript) return null;

    // 从 editConfig.clips 中找到当前 clip 的 duration
    const clipInfo = editConfig?.clips?.find(c => c.clipId === videoClip?.id);
    const clipDuration = clipInfo?.duration ?? 0;

    // clipDuration <= 0 时降级为全文
    if (clipDuration <= 0) {
      return {
        text: displaySubtitleScript,
        startOffset: 0,
        endOffset: clipDuration || 0,
        textStart: 0,
        textEnd: displaySubtitleScript.length,
      };
    }

    const segments = splitSubtitleSegments(displaySubtitleScript, clipDuration, 1.0);

    // segments 为空时降级为全文
    if (segments.length === 0) {
      return {
        text: displaySubtitleScript,
        startOffset: 0,
        endOffset: clipDuration,
        textStart: 0,
        textEnd: displaySubtitleScript.length,
      };
    }

    // 播放时使用 localTime，暂停时使用 0（显示第一段）
    const localTime = isPlaying ? (currentPlaybackInfo?.localTime ?? 0) : 0;

    const rangedSegments = mapSubtitleSegmentsToTextRanges(displaySubtitleScript, segments);
    const segment = getCurrentSegment(rangedSegments, localTime);
    if (!segment) return null;

    const matchedRange = rangedSegments.find(
      (item) =>
        item.startOffset === segment.startOffset &&
        item.endOffset === segment.endOffset &&
        item.text === segment.text,
    );

    return matchedRange ?? null;
  }, [displaySubtitleScript, videoClip?.id, editConfig, currentPlaybackInfo?.localTime, isPlaying]);

  const currentSubtitleRuns = useMemo(() => {
    if (!displaySubtitleScript) return [] as SubtitleTextRun[];
    if (!currentSubtitleSegment) {
      return [{ text: displaySubtitleScript }] as SubtitleTextRun[];
    }

    if (typeof currentSubtitleSegment.textStart !== 'number' || typeof currentSubtitleSegment.textEnd !== 'number') {
      return [{ text: currentSubtitleSegment.text }] as SubtitleTextRun[];
    }

    return sliceSubtitleTextRuns(
      displaySubtitleScript,
      Array.isArray(videoClip?.subtitleStyleSpans) ? videoClip.subtitleStyleSpans : undefined,
      { start: currentSubtitleSegment.textStart, end: currentSubtitleSegment.textEnd },
    );
  }, [currentSubtitleSegment, displaySubtitleScript, videoClip?.subtitleStyleSpans]);

  const currentClipLocalTime = isPlaying ? (currentPlaybackInfo?.localTime ?? 0) : 0;
  const activeClipEffects = useMemo(() => {
    if (!videoClip) return [];
    return getActiveClipEffectItems(videoClip.effectItems, currentClipLocalTime, activeClipDuration);
  }, [activeClipDuration, currentClipLocalTime, videoClip]);

  useClipEffectsPlayer({
    clip: videoClip ?? null,
    localTime: currentClipLocalTime,
    isPlaying,
    clipDuration: activeClipDuration,
  });

  const renderSubtitleRuns = (runs: SubtitleTextRun[]) => (
    <>
      {runs.map((run, index) => {
        const inlineStyle = resolveSubtitleInlineStyle(run.style);
        const inferredInlineScale = resolveSubtitleInlineFontScale(inlineStyle);
        return (
          <span
            key={`${run.text}-${index}`}
            style={inlineStyle ? {
              color: inlineStyle.color || subtitleAppearance.textColor,
              fontSize: inferredInlineScale
                ? `calc(${subtitleStyle.fontSize} * ${inferredInlineScale})`
                : inlineStyle.fontSize ? `${inlineStyle.fontSize}px` : undefined,
              fontWeight: inlineStyle.bold ? 700 : undefined,
              fontStyle: inlineStyle.italic ? 'italic' : undefined,
              fontFamily: inlineStyle.fontName || undefined,
            } : undefined}
          >
            {run.text}
          </span>
        );
      })}
    </>
  );

  const renderVideoMode = () => (
    <div ref={previewContainerRef} className="relative w-full h-full bg-black">
      {/* 视频铺满 */}
      <video
        ref={videoRef}
        src={videoClip?.videoUrl}
        className="w-full h-full object-contain"
      />

      {activeClipEffects.some((effect) => !!effect.sticker) && (
        <div
          className="absolute"
          style={getContainedMediaRect(previewContainerSize.width, previewContainerSize.height, videoAspectRatio)}
        >
          <ClipEffectsRenderer effectItems={activeClipEffects} baseSubtitleStyle={subtitleTrackStyle} />
        </div>
      )}

      {/* 字幕区 */}
      {subtitleEnabled && trackEnabled.subtitle && displaySubtitleScript && currentSubtitleRuns.length > 0 && (
        <div
          className="absolute left-1/2 text-center px-4"
          style={{
            ...subtitleContainerPosition,
            maxWidth: subtitleStyle.maxWidth,
            width: '100%'
          }}
        >
          <p
            className="font-medium"
            style={subtitleTextStyle}
          >
            {renderSubtitleRuns(currentSubtitleRuns)}
          </p>
        </div>
      )}

      {/* 叠加层 */}
      {showOverlays && (
        <>
          {(videoClip?.description || videoClip?.cameraWork) && (
            <div className="absolute left-4 bottom-16 bg-black/60 backdrop-blur-sm rounded-lg p-3 max-w-md">
              <div className="space-y-2 text-xs text-gray-200">
                {videoClip.description && (
                  <div>
                    <span className="text-gray-400">{t('editor:previewTab.scene')}：</span>
                    <span>{videoClip.description}</span>
                  </div>
                )}
                {videoClip.cameraWork && (
                  <div>
                    <span className="text-gray-400">{t('editor:previewTab.camera')}：</span>
                    <span>{videoClip.cameraWork}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="absolute right-4 bottom-16 space-y-2">
            {hasScriptAudio && (
              <audio
                ref={setAudioRef}
                src={videoClip?.scriptAudioUrl}
                controls
                className="w-64 h-8 opacity-80 hover:opacity-100 transition-opacity block"
              />
            )}
            {hasSoundEffect && (
              <audio
                src={videoClip?.soundEffectAudioUrl}
                controls
                className="w-64 h-8 opacity-80 hover:opacity-100 transition-opacity block"
              />
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderImageMode = () => {
    const imageMap: Record<string, string | undefined> = {
      start: videoClip?.startImage,
      end: videoClip?.endImage,
      storyboard: videoClip?.storyboardImage,
    };
    const labelMap: Record<string, string> = {
      start: t('editor:previewTab.startImage'),
      end: t('editor:previewTab.endImage'),
      storyboard: t('editor:previewTab.storyboardImage'),
    };

    const primaryImage = imageMap[mainImage];
    // 其他有图片的缩略图
    const thumbnails = (['start', 'end', 'storyboard'] as const)
      .filter(key => key !== mainImage && imageMap[key]);

    const imageSubtitleLayout = resolveSubtitleLayout(imageAspectRatio, subtitleTrackStyle?.fontSize ?? 18);
    const imageSubtitleStyle = {
      fontSize: `${imageSubtitleLayout.fontSizeScale}rem`,
      maxWidth: `${imageSubtitleLayout.maxWidthPercent}%`,
      bottom: imageSubtitleLayout.bottomOffset,
    };
    const imageSubtitleContainerPosition = subtitleTrackStyle?.position === 'center'
      ? { top: '50%', transform: 'translate(-50%, -50%)' }
      : { bottom: imageSubtitleStyle.bottom, transform: 'translateX(-50%)' };

    return (
      <div ref={previewContainerRef} className="relative w-full h-full bg-black overflow-hidden">
        {primaryImage ? (
          <img ref={imageRef} src={primaryImage} alt={labelMap[mainImage]} className="w-full h-full object-contain" />
        ) : isGenerating ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-orange-400 font-medium">{t('editor:previewTab.imageGenerating')}</p>
            </div>
          </div>
        ) : null}

        {activeClipEffects.some((effect) => !!effect.sticker) && (
          <div
            className="absolute"
            style={getContainedMediaRect(previewContainerSize.width, previewContainerSize.height, imageAspectRatio)}
          >
            <ClipEffectsRenderer effectItems={activeClipEffects} baseSubtitleStyle={subtitleTrackStyle} />
          </div>
        )}

        {thumbnails.length > 0 && (
          <div className="absolute right-4 top-4 flex flex-col gap-2">
            {thumbnails.map(key => (
              <div
                key={key}
                className="cursor-pointer transition-transform hover:scale-105"
                onClick={() => setMainImage(key)}
              >
                <div className="relative group">
                  <img
                    src={imageMap[key]!}
                    alt={labelMap[key]}
                    className="w-32 h-20 object-contain rounded-lg border-2 border-white/50 shadow-lg"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs font-medium">{labelMap[key]}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {subtitleEnabled && trackEnabled.subtitle && displaySubtitleScript && currentSubtitleRuns.length > 0 && (
          <div
            className="absolute left-1/2 text-center px-4"
            style={{
              ...imageSubtitleContainerPosition,
              maxWidth: imageSubtitleStyle.maxWidth,
              width: '100%'
            }}
          >
            <p
              className="font-medium"
              style={{
                ...subtitleTextStyle,
                fontSize: imageSubtitleStyle.fontSize,
              }}
            >
              {renderSubtitleRuns(currentSubtitleRuns)}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderPlaceholder = () => (
    <div className="flex items-center justify-center h-full bg-gray-800">
      <div className="text-center">
        {isGenerating ? (
          <>
            <div className="w-16 h-16 mx-auto mb-4 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-orange-400 font-medium">{t('common:generating')}</p>
          </>
        ) : (
          <>
            <VideoCameraIcon className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">{t('editor:previewTab.noPreviewContent')}</p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-4xl flex flex-col gap-2">
      <div className="relative w-full aspect-video bg-gray-800 overflow-hidden border border-gray-700/50">
        {hasVideo ? renderVideoMode() : (hasImages || isGenerating) ? renderImageMode() : renderPlaceholder()}
      </div>
    </div>
  );
}
